// Write-side counterpart to applyTeamType (organigram-graph-normalize.ts), import-free for the
// same reason: the spec loads this module directly and must not pull in OrganigramApiService with
// its DI / Firebase chain.
//
// The UI models every grouping layer as a node type of its own (department / division / team), but
// the backend's NodeType enum knows only human | agent | team - the layer travels inside the
// required `data` payload instead. Sending the UI type verbatim is a 422, so every create/update
// body is translated here first. The backend models (openapi.json, * = required):
//
//   agent   -> AgentData   { agent_id*, summary, location_id }
//   human   -> HumanData   { email*, uid*, name*, phone_number, location_id }
//   team    -> TeamData    { type* }                          division | department | team
//   trigger -> TriggerData { user_id*, account_id, allow_sensitive_actions }
//   pipeline -> PipelineData { pipeline_id* }
//
// `email` is format-validated, so a blank one is rejected too - the create forms have to ask for
// it. `location_id` is a REFERENCE to an organigram_locations document, which the backend resolves
// itself and rejects with a 422 when it does not exist - so callers run
// OrganigramLocationStore.ensureBackendLocation before sending one. It replaced the embedded
// `location` object the backend wanted between 2026-07-31 and 2026-08-05. TeamData has neither.
//
// Keys the backend does not declare (guidelines, and the legacy locationId on older nodes) are
// passed through untouched: they are dropped server-side, and filtering them here would make this a
// field whitelist rather than a type translation, silently breaking the day the backend adopts them.

// 'trigger' joined this union on 2026-08-05, when the backend's NodeType enum took it. Before that
// it was deliberately absent so a POST carrying one was a COMPILE error rather than a 422.
// 'pipeline' joined on 2026-09-03 the same way (NodeType.PIPELINE, PipelineData = pipeline_id).
export type OrgWriteNodeType = 'human' | 'agent' | 'team' | 'department' | 'division' | 'trigger' | 'pipeline';

// The UI types that all collapse onto the backend's single 'team' NodeType.
const TEAM_LAYERS: string[] = ['team', 'department', 'division'];

export interface NodeWriteBody {
  name?: string;
  type?: OrgWriteNodeType;
  data?: Record<string, any>;
}

// Translate one create/update body into what the backend accepts. A body without a `type` is a
// partial PATCH whose data shape cannot be derived, so it passes through unchanged - callers that
// patch `data` alone must merge it into the node's existing data object (see buildNodePatch).
// The input is never mutated.
export function toBackendNodeWrite<T extends NodeWriteBody>(body: T): T {
  const type = body && body.type;
  if (!type) { return body; }
  const data = { ...(body.data || {}) };

  if (TEAM_LAYERS.indexOf(type) >= 0) {
    // The layer the user picked becomes data.type; the node type is always 'team'.
    return { ...body, type: 'team', data: { ...data, type } } as T;
  }

  if (type === 'human') {
    return { ...body, data: {
      ...data,
      // Optional on the backend since 2026-07-31, but still sent blank rather than omitted so a
      // PATCH that replaces `data` wholesale does not silently drop a number the node already had.
      phone_number: data.phone_number || '',
      email: data.email || '',
      // The person's own mona account, which the org forms do not ask for. Required by the model,
      // so send it blank rather than omit it.
      uid: data.uid || '',
      // HumanData carries the name a second time, next to the node's own.
      name: data.name || body.name || ''
    } } as T;
  }

  // agent, trigger and pipeline: data is already AgentData / TriggerData / PipelineData. Their
  // required fields (agent_id, user_id, pipeline_id) are enforced by the forms, so an empty one is
  // left to fail loudly rather than be papered over here.
  //
  // A trigger's schedule and message are not declared by TriggerData YET - that part of the contract
  // is still being written. They travel in `data` like any other undeclared key: dropped server-side
  // today, kept the day the fields land. See createTriggerNode.
  return body;
}
